import React, { useEffect, useRef, forwardRef, useState, useCallback } from "react";
import "../../assets/css/chat/chatComponent.css";
import AgentService from "../../services/api/agents";
import { API_RESPONSE, default_size_messages, planAction, projectJobStatus, projectType } from "../../settings/shared/constants";
import ChatMessage, { dedupeMessages } from "./ChatMessage";
import { copyToClipboard, mergeAssistantMessages } from "./chatUtils";
import TextareaInput from "./TextareaInput";
import { useChatMessages } from "./useChatMessages";
import { useWebSocket } from "../../services/websocket/WebSocketContext";
import { showNotificationGlobal } from "../common/NotificationProvider";
import {
  appendProjectJsonPart,
  areAllRequiredPlanQuestionsAnswered,
  buildPlanChatSubmitBody,
  buildPlanRequestBody,
  buildPlanUserPrompt,
  isPlanAppType,
  isPlanChatMessage,
  PLAN_CHAT_MESSAGES_EVENT,
  PLAN_WORKSPACE_MODE_EVENT,
  readComposerMode,
  readPlanWorkspaceBuildMode,
  toPlanAnswerMap,
  toPlanDtoMessage,
  writeComposerMode,
  writePlanProgress,
} from "./planUtils";

const SCROLL_LOAD_THRESHOLD_PX = 96;

const ChatComponent = forwardRef(({
  isChatVisible,
  messages,
  projectId,
  onPreviewUrlChange,
  onChatActivity,
  projectStatus,
  projectAppType,
  chatWidth = 400,
  messagesPageSize = default_size_messages,
  messagesHasMore = false,
  findingChatDraft = null,
}, ref) => {
  const [composerAppType, setComposerAppType] = useState(() => {
    const stored = readComposerMode(projectId);
    if (stored) return stored;
    const fromProject = String(projectAppType || "").toUpperCase();
    return fromProject || "APP";
  });

  useEffect(() => {
    const stored = readComposerMode(projectId);
    if (stored) {
      setComposerAppType(stored);
      return;
    }
    const fromProject = String(projectAppType || "").toUpperCase();
    if (fromProject) setComposerAppType(fromProject);
  }, [projectId, projectAppType]);

  const handleComposerAppTypeChange = useCallback((value) => {
    const normalized = String(value || "").toUpperCase();
    setComposerAppType(normalized);
    writeComposerMode(projectId, normalized);
  }, [projectId]);
  const [state, setState] = useChatMessages(messages, projectId, projectStatus, onPreviewUrlChange);

  useEffect(() => {
    if (typeof onChatActivity === 'function') {
      onChatActivity(Boolean(state.streamingMessage || state.isTyping));
    }
  }, [state.streamingMessage, state.isTyping, onChatActivity]);

  useEffect(() => () => {
    if (typeof onChatActivity === 'function') onChatActivity(false);
  }, [onChatActivity]);

  useEffect(() => {
    const onMode = (event) => {
      if (event?.detail?.projectId && String(event.detail.projectId) !== String(projectId || "")) return;
      const mode = String(event?.detail?.mode || "").toUpperCase();
      if (!mode) return;
      setComposerAppType(mode);
    };
    window.addEventListener(PLAN_WORKSPACE_MODE_EVENT, onMode);
    return () => window.removeEventListener(PLAN_WORKSPACE_MODE_EVENT, onMode);
  }, [projectId]);

  useEffect(() => {
    if (readPlanWorkspaceBuildMode(projectId) || readComposerMode(projectId) === "APP") return;
    const pending = (state.chatMessages || []).some(
      (msg) => isPlanChatMessage(msg) && msg.planStatus !== "submitted"
    );
    if (pending) handleComposerAppTypeChange("PLAN");
  }, [state.chatMessages, handleComposerAppTypeChange, projectId]);

  useEffect(() => {
    const onPlanBuild = (event) => {
      if (event?.detail?.projectId && String(event.detail.projectId) !== String(projectId || "")) return;
      const userMessage = event?.detail?.userMessage;
      if (!userMessage) return;
      handleComposerAppTypeChange("APP");
      setState((prev) => ({
        ...prev,
        chatMessages: [...(prev.chatMessages || []), userMessage],
        chatInput: "",
        isTyping: true,
        planAwaitingResponse: false,
      }));
    };
    window.addEventListener("sa-plan-start-build", onPlanBuild);
    return () => window.removeEventListener("sa-plan-start-build", onPlanBuild);
  }, [projectId, handleComposerAppTypeChange, setState]);
  useEffect(() => {
    try {
      window.dispatchEvent(
        new CustomEvent(PLAN_CHAT_MESSAGES_EVENT, {
          detail: {
            projectId,
            messages: state.chatMessages || [],
            composerAppType,
            isTyping: state.isTyping,
            planAwaitingResponse: state.planAwaitingResponse,
          },
        })
      );
    } catch {
      /* ignore */
    }
  }, [
    projectId,
    state.chatMessages,
    state.isTyping,
    state.planAwaitingResponse,
    composerAppType,
  ]);
  const { checkConnection, reconnectWithRetry, isConnected, forceReconnect } = useWebSocket();
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const isAtBottomRef = useRef(true);
  const reconnectAttemptRef = useRef(false);
  const connectionCheckedRef = useRef(false);
  const initialAutoScrollDoneRef = useRef(false);
  const nextMessagesPageRef = useRef(1);
  const loadingOlderRef = useRef(false);
  const hasMoreOlderRef = useRef(messagesHasMore);
  const [hasMoreOlder, setHasMoreOlder] = useState(messagesHasMore);
  const [loadingOlder, setLoadingOlder] = useState(false);

  useEffect(() => {
    hasMoreOlderRef.current = messagesHasMore;
    setHasMoreOlder(messagesHasMore);
    nextMessagesPageRef.current = 1;
  }, [projectId, messagesHasMore]);

  const ensureWebSocketConnection = useCallback(async () => {
    if (checkConnection()) {
      return true;
    }
    const connected = await reconnectWithRetry(5, 800);
    return connected;
  }, [checkConnection, reconnectWithRetry]);

  useEffect(() => {
    if (!isChatVisible) {
      return;
    }
    const checkAndHandleConnection = async () => {
      const connected = checkConnection();

      if (connected) {
        if (!connectionCheckedRef.current) {
          connectionCheckedRef.current = true;
        }
      } else {
        connectionCheckedRef.current = false;
        if (!reconnectAttemptRef.current) {
          reconnectAttemptRef.current = true;
          const reconnected = await reconnectWithRetry(5, 800);

          if (reconnected) {
            connectionCheckedRef.current = true;
          }

          reconnectAttemptRef.current = false;
        }
      }
    };
    const timeoutId = setTimeout(() => {
      checkAndHandleConnection();
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isChatVisible, checkConnection, reconnectWithRetry]);

  useEffect(() => {
    if (!isConnected && !reconnectAttemptRef.current) {
      reconnectAttemptRef.current = true;
      const timeoutId = setTimeout(() => {
        forceReconnect();
        reconnectAttemptRef.current = false;
      }, 1000);

      return () => {
        clearTimeout(timeoutId);
        reconnectAttemptRef.current = false;
      };
    } else if (isConnected) {
      reconnectAttemptRef.current = false;
      if (!connectionCheckedRef.current) {
        connectionCheckedRef.current = true;
      }
    }
  }, [isConnected, forceReconnect]);

  const loadOlderMessages = useCallback(async () => {
    const el = chatMessagesRef.current;
    if (!el || !projectId || loadingOlderRef.current || !hasMoreOlderRef.current) return;

    loadingOlderRef.current = true;
    setLoadingOlder(true);
    const page = nextMessagesPageRef.current;
    const prevScrollHeight = el.scrollHeight;
    const prevScrollTop = el.scrollTop;

    try {
      const response = await AgentService.getProject(page, { id: projectId }, messagesPageSize);
      if (response?.status !== API_RESPONSE.SUCCESS) return;

      const olderRaw = response?.projectDTO?.messages ?? [];
      if (!olderRaw.length) {
        hasMoreOlderRef.current = false;
        setHasMoreOlder(false);
        return;
      }

      const older = mergeAssistantMessages(olderRaw, projectJobStatus.SUCCESS, projectId);
      setState((prev) => ({
        ...prev,
        chatMessages: dedupeMessages([...older, ...prev.chatMessages]),
      }));

      nextMessagesPageRef.current = page + 1;
      if (olderRaw.length < messagesPageSize) {
        hasMoreOlderRef.current = false;
        setHasMoreOlder(false);
      }

      requestAnimationFrame(() => {
        const node = chatMessagesRef.current;
        if (!node) return;
        const delta = node.scrollHeight - prevScrollHeight;
        node.scrollTop = prevScrollTop + delta;
      });
    } catch (err) {
      console.error("[ChatComponent] loadOlderMessages failed:", err);
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [projectId, messagesPageSize, setState]);

  const handleScroll = useCallback(() => {
    if (!chatMessagesRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = chatMessagesRef.current;
    const scrollableHeight = scrollHeight - clientHeight;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const atBottom = distanceFromBottom <= 1;

    isAtBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
    setShowScrollTop(scrollTop > 0);
    setShowScrollBottom(!atBottom && scrollableHeight > 0);

    if (
      scrollTop <= SCROLL_LOAD_THRESHOLD_PX &&
      hasMoreOlderRef.current &&
      !loadingOlderRef.current
    ) {
      loadOlderMessages();
    }
  }, [loadOlderMessages]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => {
      isAtBottomRef.current = true;
      setIsAtBottom(true);
      handleScroll();
    }, 100);
  }, [handleScroll]);

  const scrollToTop = useCallback(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => {
        handleScroll();
      }, 100);
    }
  }, [handleScroll]);

  // Auto-scroll effect: uses ref so it always reads the latest scroll position,
  // never a stale closure value from a previous render cycle.
  useEffect(() => {
    const container = chatMessagesRef.current;
    if (!container) return;

    // On first render, always jump to bottom so latest messages are visible.
    if (!initialAutoScrollDoneRef.current) {
      initialAutoScrollDoneRef.current = true;
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      isAtBottomRef.current = true;
      setIsAtBottom(true);
      return;
    }

    // Only auto-scroll if the user is already at the bottom.
    // Reading from the ref (not state) avoids the stale closure problem.
    if (isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [state.chatMessages]);

  useEffect(() => {
    const chatMessagesElement = chatMessagesRef.current;
    if (chatMessagesElement) {
      chatMessagesElement.addEventListener('scroll', handleScroll);
      handleScroll();

      return () => {
        chatMessagesElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll, state.chatMessages]);

  const handleChatSubmit = async (e, submitData) => {
    e.preventDefault();
    if (submitData) {
      const isConnected = await ensureWebSocketConnection();

      if (!isConnected) {
        console.error('WebSocket connection failed. Cannot proceed with submission.');
        showNotificationGlobal("Connection lost. Reconnect and try again.", "error", "topRight");
        return;
      }

      const xrayJobType = submitData?.xrayJobType || "";
      const isXrayModernize = String(xrayJobType).toUpperCase() === "XRAY_MODERNIZE";
      const planMode =
        !xrayJobType &&
        (isPlanAppType(submitData?.appType) || isPlanAppType(composerAppType));
      if (planMode) {
        handleComposerAppTypeChange("PLAN");
      }
      const userMessage = {
        role: 'user',
        content: submitData?.text,
        createdAt: new Date(),
        projectId: projectId,
        prompt: submitData?.text,
        type: isXrayModernize ? projectType.PLAN : (planMode ? projectType.PLAN : projectType.RESUME),
        appType: planMode ? projectType.PLAN : submitData?.appType,
      };

      const planSubmit = planMode
        ? buildPlanChatSubmitBody({
            prompt: submitData?.text,
            projectId,
            chatMessages: state.chatMessages,
          })
        : null;
      const payload = planMode
        ? planSubmit.body
        : {
            ...userMessage,
            // Persist composer APP / APP_AGENT / WEBSITE on the project for build + projectInfo.
            appType: xrayJobType
              ? (String(projectAppType || composerAppType || "XRAY").toUpperCase())
              : (submitData?.appType || composerAppType || "APP"),
          };
      if (xrayJobType) {
        payload.xrayJobType = xrayJobType;
      }
      if (submitData?.json != null) {
        payload.json = submitData.json;
      }

      const formData = new FormData();
      appendProjectJsonPart(formData, payload);

      const isXray = String(submitData?.appType || "").toUpperCase() === "XRAY";
      if (!isXray) {
        submitData.uploadedFiles.forEach(item => {
          formData.append("file", item.file);
        });
      }

      setState((prevState) => ({
        ...prevState,
        chatMessages: [...prevState.chatMessages, userMessage],
        chatInput: "",
        isTyping: true,
        planAwaitingResponse: planMode,
      }));

      try {
        const response = await AgentService.createProject(formData);
        if (response?.status === API_RESPONSE.FAILURE) {
          showNotificationGlobal(response?.errorMessage || "Project creation failed.", "error", "topRight");
          setState((prev) => ({
            ...prev,
            isTyping: false,
            planAwaitingResponse: false,
          }));
          return;
        }
      } catch (error) {
        showNotificationGlobal(error?.message || "Something went wrong. Please try again.", "error", "topRight");
        setState((prev) => ({
          ...prev,
          isTyping: false,
          planAwaitingResponse: false,
        }));
      }
    }
  };

  const handlePlanProgress = (planMessage, answers, additionalInput = "") => {
    writePlanProgress(projectId, planMessage, {
      answers,
      additionalInput,
      submitted: false,
    });
    setState((prev) => ({
      ...prev,
      chatMessages: prev.chatMessages.map((msg) =>
        msg.id === planMessage.id
          ? { ...msg, planAnswers: answers, planAdditionalInput: additionalInput }
          : msg
      ),
    }));
  };

  const handlePlanContinue = async (planMessage, answers, additionalInput = "") => {
    const connected = await ensureWebSocketConnection();
    if (!connected) {
      showNotificationGlobal("Connection lost. Reconnect and try again.", "error", "topRight");
      return;
    }

    if (!areAllRequiredPlanQuestionsAnswered(planMessage?.planQuestions, answers)) {
      showNotificationGlobal("Please answer the remaining planning questions.", "info", "topRight");
      return;
    }

    handleComposerAppTypeChange("PLAN");
    const answerMap = toPlanAnswerMap(planMessage?.planQuestions, answers);
    const extraInput = String(additionalInput || "").trim();
    const answerContent = buildPlanUserPrompt(planMessage?.planQuestions, answers, extraInput);

    writePlanProgress(projectId, planMessage, {
      answers,
      additionalInput: extraInput,
      submitted: true,
    });

    const planDtoMessage = toPlanDtoMessage({
      id: planMessage.id,
      role: "assistant",
      content: answerContent,
      type: projectType.PLAN,
      planMessage: planMessage.planMessage || planMessage.planQuestions || [],
      createdAt: planMessage.createdAt,
    });

    setState((prev) => ({
      ...prev,
      chatMessages: prev.chatMessages.map((msg) =>
        msg.id === planMessage.id
          ? {
              ...msg,
              type: projectType.PLAN,
              content: answerContent,
              planStatus: "submitted",
              planAnswers: answers,
              planAdditionalInput: extraInput,
              jobStatus: projectJobStatus.SUCCESS,
            }
          : msg
      ),
      isTyping: true,
      planAwaitingResponse: true,
    }));

    const formData = new FormData();
    appendProjectJsonPart(
      formData,
      buildPlanRequestBody({
        prompt: null,
        projectId,
        planAction: planAction.RESPOND,
        answers: answerMap,
        additionalInput: extraInput,
        messages: [planDtoMessage],
      })
    );

    const revertPlanContinue = () => {
      writePlanProgress(projectId, planMessage, {
        answers,
        additionalInput: extraInput,
        submitted: false,
      });
      setState((prev) => ({
        ...prev,
        isTyping: false,
        planAwaitingResponse: false,
        chatMessages: prev.chatMessages.map((msg) =>
          msg.id === planMessage.id
            ? { ...msg, planStatus: "active", content: planMessage.content }
            : msg
        ),
      }));
    };

    try {
      const response = await AgentService.createProject(formData);
      if (response?.status === API_RESPONSE.FAILURE) {
        showNotificationGlobal(response?.errorMessage || "Could not continue planning.", "error", "topRight");
        revertPlanContinue();
      }
    } catch (error) {
      showNotificationGlobal(error?.message || "Something went wrong. Please try again.", "error", "topRight");
      revertPlanContinue();
    }
  };

  const handleStop = async () => {
    const formData = new FormData();
    formData.append(
      "project",
      new Blob(
        [JSON.stringify({ projectId: projectId, type: projectType.INTERRUPT })],
        { type: "application/json" }
      )
    );
    await AgentService.createProject(formData);
  };

  const handleMessageFeedback = (messageId, feedbackType) => {
    setState((prevState) => ({
      ...prevState,
      messageFeedback: {
        ...prevState.messageFeedback,
        [messageId]: feedbackType
      }
    }));
  };

  const messageList = dedupeMessages(state.chatMessages);

  return (
    <div
      ref={ref}
      className={`main-right main-right--studio ${isChatVisible ? 'visible' : 'hidden'}`}
      style={isChatVisible ? { width: `${chatWidth}px`, minWidth: `${chatWidth}px` } : {}}
    >
      <div className="chat-messages-shell">
      <div
        ref={chatMessagesRef}
        className="chat-messages chat-messages--studio font-inter-regular"
      >
        {(hasMoreOlder || loadingOlder) && (
          <div className="chat-history-anchor">
            {loadingOlder ? (
              <div className="chat-history-pill chat-history-pill--loading" role="status" aria-live="polite">
                <span className="chat-history-pill-shimmer" />
                <i className="fa-solid fa-history" aria-hidden="true" />
                Loading earlier messages…
              </div>
            ) : (
              <button
                type="button"
                className="chat-history-pill chat-history-pill--action"
                onClick={() => loadOlderMessages()}
              >
                <i className="fa-solid fa-arrow-up" aria-hidden="true" />
                Earlier messages — scroll up or tap to load
              </button>
            )}
          </div>
        )}

        {state.chatMessages?.length === 0 && (
          <div className="chat-empty-state chat-empty-state--studio">
            <div className="chat-empty-state-orbit">
              <i className="fa-solid fa-comments" />
            </div>
            <p className="chat-empty-state-title">Build with your assistant</p>
            <p className="chat-empty-state-sub">Describe what you want — code, UI, and workflows update from here.</p>
          </div>
        )}

        {messageList?.map((message, index) => (
          <ChatMessage
            key={message.id != null ? String(message.id) : `${message.role}-${String(message.createdAt)}-${index}`}
            message={message}
            messageFeedback={state.messageFeedback}
            onFeedback={handleMessageFeedback}
            onCopy={copyToClipboard}
            onPlanContinue={handlePlanContinue}
            onPlanProgress={handlePlanProgress}
          />
        ))}

        {state.isTyping && (
          <div className="typing-indicator">
            <div className={`typing-dots ${state.planAwaitingResponse ? "typing-dots--plan" : ""}`}>
              {state.planAwaitingResponse ? (
                <>
                  <i className="fa-solid fa-list-check" aria-hidden="true" />
                  <span>Planning your application…</span>
                </>
              ) : (
                <>
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                </>
              )}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {showScrollTop && (
        <button
          className="chat-scroll-button chat-scroll-top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          title="Scroll to top"
        >
          <i className="fa-solid fa-arrow-up" />
        </button>
      )}

      {showScrollBottom && (
        <button
          className="chat-scroll-button chat-scroll-bottom"
          onClick={scrollToBottom}
          aria-label="Scroll to bottom"
          title="Scroll to bottom"
        >
          <i className="fa-solid fa-arrow-down" />
        </button>
      )}
      </div>

      <div className="p-2">
        <TextareaInput
          onSubmit={handleChatSubmit}
          onStop={handleStop}
          isStreaming={state.streamingMessage}
          enableAtMentions
          projectAppType={composerAppType}
          onAppTypeChange={handleComposerAppTypeChange}
          findingChatDraft={findingChatDraft}
        />
      </div>
    </div>
  );
});

ChatComponent.displayName = 'ChatComponent';

export default ChatComponent;